import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiUploadCloud, FiX } from "react-icons/fi";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "styles/teacher.css";
import "styles/admin.css";

const FIELD_LABELS = {
  name: "Ism",
  surname: "Familiya",
  username: "Username",
  password: "Parol",
};

const REQUIRED_FIELDS = ["name", "surname", "username", "password"];

const formVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1,
    },
  },
};

const childVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0 },
};

const StudentAddPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    username: "",
    password: "",
    confirmPassword: "",
    note: "",
    image: null,
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image") {
      setFormData((prev) => ({ ...prev, image: files?.[0] ?? null }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const removeImage = (e) => {
    e.stopPropagation();
    setFormData((prev) => ({ ...prev, image: null }));
    const fileInput = document.getElementById("imageInput");
    if (fileInput) fileInput.value = "";
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      toast.error("Parollar mos emas!");
      return false;
    }

    for (const field of REQUIRED_FIELDS) {
      if (!formData[field]?.trim()) {
        toast.error(`${FIELD_LABELS[field]} kiritilmagan!`);
        return false;
      }
    }

    if (formData.password.length < 6) {
      toast.error("Parol kamida 6 belgidan iborat bo'lishi kerak!");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error("Tizimga kirish kerak!");
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();
      data.append("FirstName", formData.name.trim());
      data.append("LastName", formData.surname.trim());
      data.append("Username", formData.username.trim());
      data.append("Password", formData.password);
      data.append("ConfirmPassword", formData.confirmPassword);
      data.append("RoleId", "1");

      if (formData.note?.trim()) data.append("Bio", formData.note.trim());
      if (formData.image) data.append("Image", formData.image);

      await axios.post(
        "https://algoritm-api-edu.uz/api/teacher/Users/Register",
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success("Talaba muvaffaqiyatli qo'shildi!");

      setFormData({
        name: "",
        surname: "",
        username: "",
        password: "",
        confirmPassword: "",
        note: "",
        image: null,
      });

      const fileInput = document.getElementById("imageInput");
      if (fileInput) fileInput.value = "";
    } catch (error) {
      const err = error.response?.data;
      if (err?.errors) {
        if (Array.isArray(err.errors)) {
          err.errors.forEach((msg) => toast.error(`${msg}`));
        } else {
          Object.entries(err.errors || {}).forEach(([field, messages]) => {
            const msgs = Array.isArray(messages) ? messages : [messages];
            msgs.forEach((msg) => toast.error(`${field}: ${msg}`));
          });
        }
      } else {
        toast.error(err?.title || err?.message || "Xato yuz berdi");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="register-container"
      initial="hidden"
      animate="visible"
      variants={formVariants}
    >
      <form className="register-form" onSubmit={handleSubmit}>
        <motion.div className="form-group" variants={childVariants}>
          <label>
            Ism <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <input
            type="text"
            name="name"
            placeholder="Ismni kiriting (15 belgigacha)"
            maxLength={15}
            value={formData.name}
            onChange={handleChange}
            disabled={loading}
            required
          />
        </motion.div>

        <motion.div className="form-group" variants={childVariants}>
          <label>
            Familiya <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <input
            type="text"
            name="surname"
            placeholder="Familiyani kiriting (15 belgigacha)"
            maxLength={15}
            value={formData.surname}
            onChange={handleChange}
            disabled={loading}
            required
          />
        </motion.div>

        <motion.div className="form-group" variants={childVariants}>
          <label>
            Username <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <input
            type="text"
            name="username"
            placeholder="Username kiriting (maks 32 belgi)"
            maxLength={32}
            value={formData.username}
            onChange={handleChange}
            disabled={loading}
            required
          />
        </motion.div>

        <div className="form-row">
          <motion.div className="form-group" variants={childVariants}>
            <label>
              Parol <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="password"
              name="password"
              placeholder="Parolni kiriting (min 6 belgi)"
              minLength={6}
              maxLength={32}
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              required
            />
          </motion.div>

          <motion.div className="form-group" variants={childVariants}>
            <label>
              Parolni tasdiqlang <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="password"
              name="confirmPassword"
              placeholder="Parolni qayta kiriting"
              minLength={6}
              maxLength={32}
              value={formData.confirmPassword}
              onChange={handleChange}
              disabled={loading}
              required
            />
          </motion.div>
        </div>

        <motion.div className="form-group" variants={childVariants}>
          <label>Avatar (ixtiyoriy)</label>
          <motion.div
            className="image-upload-box"
            whileHover={{ scale: loading ? 1 : 1.03 }}
            whileTap={{ scale: loading ? 1 : 0.97 }}
            onClick={() =>
              !loading && document.getElementById("imageInput")?.click()
            }
          >
            <AnimatePresence mode="wait">
              {formData.image ? (
                <motion.div
                  key="image"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="file-info"
                >
                  <span className="file-name">{formData.image.name}</span>
                  <motion.span
                    className="remove-file"
                    whileHover={{ scale: 1.2, color: "#ef4444" }}
                    whileTap={{ scale: 0.9 }}
                    onClick={removeImage}
                  >
                    <FiX size={18} />
                  </motion.span>
                </motion.div>
              ) : (
                <motion.div
                  key="placeholder"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="upload-placeholder"
                >
                  <FiUploadCloud size={32} color="#666" />
                  <p>Rasm yuklang (PNG/JPG, maks 5MB)</p>
                </motion.div>
              )}
            </AnimatePresence>

            <input
              id="imageInput"
              type="file"
              name="image"
              accept="image/*"
              hidden
              onChange={handleChange}
              disabled={loading}
            />
          </motion.div>
        </motion.div>

        <motion.div className="form-group" variants={childVariants}>
          <label>Izoh (ixtiyoriy)</label>
          <textarea
            name="note"
            placeholder="Foydalanuvchi haqida qo'shimcha ma'lumot (maks 400 belgi)..."
            rows={4}
            maxLength={400}
            value={formData.note}
            onChange={handleChange}
            disabled={loading}
          />
        </motion.div>

        <motion.button
          type="submit"
          className="submit-btn"
          disabled={loading}
          whileHover={{ scale: loading ? 1 : 1.05 }}
          whileTap={{ scale: loading ? 1 : 0.95 }}
          variants={childVariants}
        >
          {loading ? "Qo'shilmoqda..." : "Talaba qo'shish"}
        </motion.button>
      </form>

      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </motion.div>
  );
};

export default StudentAddPage;
